/**
 * Refactored CreateSolicitudDialog - Uses modular components and custom hook
 * Original: 763 lines -> Refactored: ~80 lines
 * Backup saved at: CreateSolicitudDialog.original.js
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

// Import modular components
import {
  useCreateSolicitud,
  InmuebleSection,
  TitularSection,
  SolicitanteSection,
  SolicitudesSection
} from './CreateSolicitud';

const CreateSolicitudDialog = ({ open, onClose }) => {
  // Use custom hook for all state and logic
  const {
    proyectos,
    subdivisiones,
    titularEsSolicitante,
    formData,
    solicitudes,
    loading,
    initialLoading, // Agregado
    calculandoGarantia,
    selectedProyecto,
    // Catálogos
    categorias,
    lugares,
    items,
    fallas,
    // Handlers
    handleProyectoChange,
    handleEtapaChange,
    handleFechaEntregaChange,
    handleOrigenChange,
    handleTitularEsSolicitanteChange,
    addSolicitud,
    removeSolicitud,
    updateSolicitud,
    updateSolicitudFotos,
    handleSubmit,
    updateFormField
  } = useCreateSolicitud(onClose);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-3xl w-[95vw] sm:w-auto max-h-[90vh] flex flex-col gap-0 p-0"
        data-testid="create-solicitud-dialog"
      >
        <DialogHeader className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-[#E8E8E7] bg-white">
          <DialogTitle 
            className="text-lg sm:text-xl lg:text-2xl font-bold text-[#2D2D2D]" 
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Nuevo Requerimiento
          </DialogTitle>
        </DialogHeader>

        {/* Spinner de carga inicial */}
        {initialLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 sm:px-6">
            <Loader2 size={48} className="animate-spin text-[#9d8272] mb-4" />
            <p className="text-sm text-[#5A5A5A]">Cargando formulario...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-4 sm:py-6 space-y-3 sm:space-y-4">
          {/* 1. Datos del Inmueble */}
          <InmuebleSection
            formData={formData}
            proyectos={proyectos}
            subdivisiones={subdivisiones}
            selectedProyecto={selectedProyecto}
            calculandoGarantia={calculandoGarantia}
            onProyectoChange={handleProyectoChange}
            onEtapaChange={handleEtapaChange}
            onOrigenChange={handleOrigenChange}
            onFechaEntregaChange={handleFechaEntregaChange}
            onDepartamentoChange={(val) => updateFormField('departamento', val)}
          />

          {/* 2. Datos del Titular */}
          <TitularSection
            formData={formData}
            onFieldChange={updateFormField}
          />

          {/* 3. Checkbox y Datos del Solicitante */}
          <SolicitanteSection
            formData={formData}
            titularEsSolicitante={titularEsSolicitante}
            onTitularEsSolicitanteChange={handleTitularEsSolicitanteChange}
            onFieldChange={updateFormField}
          />

          {/* 4. Solicitudes */}
          <SolicitudesSection
            solicitudes={solicitudes}
            onAdd={addSolicitud}
            onRemove={removeSolicitud}
            onUpdate={updateSolicitud}
            onUpdateFotos={updateSolicitudFotos}
            categorias={categorias}
            lugares={lugares}
            items={items}
            fallas={fallas}
          />
          </div>

          {/* Footer fijo con botones */}
          <div className="shrink-0 bg-white px-4 sm:px-6 py-3 border-t border-[#E8E8E7]">
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose} 
                className="text-sm"
                data-testid="cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white text-sm"
                data-testid="submit-btn"
              >
                {loading ? 'Creando...' : 'Crear Requerimiento'}
              </Button>
            </div>
          </div>
        </form>
        )
      }
      </DialogContent>
    </Dialog>
  );
};

export default CreateSolicitudDialog;
